// SEO metadata builders: keyword-optimized titles and descriptions per page
// plus JSON-LD schema builders (Service, BlogPosting, BreadcrumbList).
// Business facts (NAP) must stay consistent with index.html's static
// ProfessionalService schema and the visible contact details.

export const BUSINESS = {
  name: "IT Device Services",
  phone: "+1 (682) 231-1534",
  email: "info@itdeviceservices.com",
  city: "Dallas-Fort Worth",
  region: "TX",
  country: "US",
};

const plain = (value) => {
  if (value && typeof value === "object" && "text" in value) return value.text || "";
  return value || "";
};

const oneLine = (value) => plain(value).replace(/\n/g, " ").trim();

const clip = (text, max = 158) => {
  const t = oneLine(text);
  if (t.length <= max) return t;
  return t.slice(0, max - 1).replace(/\s+\S*$/, "") + "\u2026";
};

export const PAGE_META = {
  home: {
    title: "IT Device Services | Managed Device & Endpoint Lifecycle Management in Dallas-Fort Worth, TX",
    description:
      "Endpoint lifecycle management for growing businesses: managed device management, patch management, provisioning and deployment, technology refresh, secure hardware disposal and IT consulting. Dallas-Fort Worth, TX based, serving businesses nationwide.",
  },
  services: {
    title: "IT Device Management Services: Managed, Project & Consulting",
    description:
      "12 endpoint lifecycle services across 3 areas: Managed Services (device, patch and provisioning management), Project Services (onboarding, migration, refresh, disposal) and Technology Consulting (assessments, strategy, roadmaps, procurement).",
  },
  about: {
    title: "About IT Device Services | Endpoint Lifecycle Management Experts",
    description:
      "IT Device Services helps organizations plan, deploy, manage, refresh and retire business technology. Based in Dallas-Fort Worth, TX, we work as an extension of your team across the full endpoint lifecycle.",
  },
  contact: {
    title: "Contact IT Device Services | Dallas-Fort Worth, TX | Book a Discovery Call",
    description:
      "Talk to an endpoint management expert. Email info@itdeviceservices.com, call +1 (682) 231-1534, send a message or book a discovery call. Replies within one business day. Monday to Friday, 9am to 6pm.",
  },
  blog: {
    title: "Device Management & IT Lifecycle Blog",
    description:
      "Practical articles on device refresh planning, zero-touch onboarding, patch management and responsible e-waste disposal, written for people who run businesses, not data centers.",
  },
  legal: {
    "privacy-policy": {
      title: "Privacy Policy",
      description: "How IT Device Services collects, uses and protects your personal information.",
    },
    "terms-of-service": {
      title: "Terms of Service",
      description: "The terms that govern use of the IT Device Services website and services.",
    },
    "cookie-policy": {
      title: "Cookie Policy",
      description: "The cookies this website uses and how to manage them.",
    },
  },
};

export const serviceMeta = (service) => ({
  title: `${oneLine(service.title)} Services in Dallas-Fort Worth, TX`,
  description: clip(service.teaser),
});

export const blogPostMeta = (post) => ({
  title: `${oneLine(post.title)}`,
  description: clip(post.excerpt),
});

export const breadcrumbSchema = (crumbs) => {
  const origin = window.location.origin;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      item: origin + crumb.path,
    })),
  };
};

export const serviceSchema = (service, umbrella) => {
  const origin = window.location.origin;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: oneLine(service.title),
    serviceType: `${oneLine(service.title)} (${oneLine(umbrella.title)})`,
    description: oneLine(service.teaser),
    url: `${origin}/services/${service.slug}`,
    provider: {
      "@type": "ProfessionalService",
      name: BUSINESS.name,
      telephone: BUSINESS.phone,
      email: BUSINESS.email,
      address: {
        "@type": "PostalAddress",
        addressLocality: BUSINESS.city,
        addressRegion: BUSINESS.region,
        addressCountry: BUSINESS.country,
      },
    },
    areaServed: [
      { "@type": "City", name: "Dallas" },
      { "@type": "City", name: "Fort Worth" },
      { "@type": "State", name: "Texas" },
      { "@type": "Country", name: "United States" },
    ],
  };
};

export const blogPostingSchema = (post) => {
  const origin = window.location.origin;
  const parsed = new Date(oneLine(post.date));
  const iso = Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString().slice(0, 10);
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: oneLine(post.title),
    description: oneLine(post.excerpt),
    articleSection: oneLine(post.category),
    ...(iso ? { datePublished: iso, dateModified: iso } : {}),
    url: `${origin}/blog/${post.slug}`,
    mainEntityOfPage: { "@type": "WebPage", "@id": `${origin}/blog/${post.slug}` },
    author: { "@type": "Organization", name: BUSINESS.name },
    publisher: {
      "@type": "Organization",
      name: BUSINESS.name,
      logo: { "@type": "ImageObject", url: `${origin}/favicon.png` },
    },
    image: `${origin}/assets/workspace.jpg`,
  };
};
